import {
  Icon,
  Stack,
  Typography,
} from '@ace-clublabs/shared-component-library';
import { motion } from 'framer-motion';
import { useRef } from 'react';

import ProfileImage from '@/components/shared/ProfileImage';
import useThemeStore from '@/store/ThemeStore';
import type { AgentInfo } from '../utils/AgentInfo';
import SelectionCardsSkeleton from './SelectionCardsSkeleton';

const MotionStack = motion.create(Stack);

type AgentsSelectionProps = {
  agents: AgentInfo[];
  selectedAgent: Partial<AgentInfo> | null;
  onSelectAgent: (agent: AgentInfo) => void;
  isLoading?: boolean;
};

const AgentsSelection: React.FC<AgentsSelectionProps> = ({
  agents,
  selectedAgent,
  onSelectAgent,
  isLoading = false,
}) => {
  const { isLuxuryTheme } = useThemeStore();

  const listRef = useRef<HTMLDivElement>(null);

  const handleSelectAgent = (agent: AgentInfo) => {
    onSelectAgent(agent);
    listRef.current?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  return (
    <MotionStack
      ref={listRef}
      layout
      direction='column'
      spacing={2}
      data-testid={
        isLoading
          ? 'agentsSelectionContainerLoading'
          : 'agentsSelectionContainer'
      }
      sx={{
        width: '100%',
        my: 2,
        px: 2,
        overflowY: 'scroll',
        scrollbarColor: 'grey transparent',
      }}
    >
      {isLoading ? (
        <SelectionCardsSkeleton count={4} />
      ) : (
        agents.map((agent) => {
          const isSelected =
            selectedAgent?.agentRecordId === agent.agentRecordId;
          const location = agent.location
            ? `${agent.location} Branch`
            : 'Online Only';
          return (
            <MotionStack
              layout
              key={agent.agentRecordId}
              direction='row'
              sx={[
                {
                  backgroundColor: 'background.default',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  borderRadius: 2,
                  padding: 2,
                  width: '100%',
                  alignItems: 'center',
                  cursor: 'pointer',
                },
                isLuxuryTheme && {
                  borderColor: 'primary.main',
                  borderWidth: '2px',
                },
                isSelected && {
                  backgroundColor: '#FCFBF7',
                  borderColor: 'primary.main',
                  borderWidth: '2px',
                },
              ]}
              onClick={() => handleSelectAgent(agent)}
            >
              <ProfileImage src={agent.image} alt={agent.name} size='small' />
              <Stack
                direction='column'
                sx={{
                  ml: 2,
                  flex: 1,
                }}
              >
                <Typography
                  variant='h6Medium'
                  sx={[
                    {
                      textAlign: 'left',
                    },
                    isSelected && {
                      color: isLuxuryTheme ? 'secondary.main' : 'inherit',
                    },
                    isLuxuryTheme && {
                      fontFamily: 'Domine',
                    },
                  ]}
                >
                  {agent.name}
                </Typography>
                <Stack direction='row' spacing={1} alignItems='center'>
                  <Icon
                    iconName={
                      isLuxuryTheme
                        ? 'location-dot-regular'
                        : 'location-dot-duotone'
                    }
                    color='primary'
                  />
                  <Typography
                    variant='body1'
                    sx={[
                      {
                        textAlign: 'left',
                      },
                      isSelected && {
                        color: isLuxuryTheme ? 'secondary.main' : 'inherit',
                      },
                    ]}
                  >
                    {location}
                  </Typography>
                </Stack>
              </Stack>
            </MotionStack>
          );
        })
      )}
    </MotionStack>
  );
};

export default AgentsSelection;
